import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { ArrowRight, Shield, Check, X, Info, Plus, Phone } from 'lucide-react';
import Header from '../../components/ui/Header';
import Footer from '../../components/Footer';
import { openCustomerPortal } from '../../utils/cn';

// /insurance — new page created 2026-05-27 from mockup. Walks the
// homeowner through the "file your claim with our quote in hand"
// pattern OpenbooQ uses everywhere else on the site (warranty page
// storm-damage callout, FAQ "Does OpenbooQ do insurance work?",
// etc.). Pass 1 ships only the hero section; the rest will be
// built section-by-section against subsequent mockup screenshots.

const EASE_OUT_EXPO = [0.16, 1, 0.3, 1];

// Machine-readable scope statement for this page. An answer engine that reads
// /insurance without this sees a page full of claim-filing steps under an
// "Insurance" nav label and concludes OpenbooQ handles claims. Every sentence
// here is a plain restatement of the "What OpenbooQ doesn't do" card that has
// been on the page since it launched — nothing new is claimed. Mirrors the
// same two Q&As on /ai and in llms.txt so the answer is identical wherever an
// engine picks it up. Both questions are backed by visible copy on this page.
const INSURANCE_SCOPE_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  '@id': 'https://openbooq.com/insurance/#scope',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Does OpenbooQ handle, file, or manage insurance claims?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'No. OpenbooQ is a retail roofing company. It does not file, negotiate, adjust, or manage insurance claims, does not work directly with insurance adjusters, does not determine coverage or guarantee that a claim will be approved, and is not a public adjuster or an insurance representative of any kind. What OpenbooQ provides is a free, line-itemed quote documenting the real cost of the work, which the homeowner can take to their own carrier and file their own claim with themselves. The material on this page is general educational information, not a coverage determination or insurance advice; the insurer decides the claim under the terms of the policy.',
      },
    },
    {
      '@type': 'Question',
      name: 'What does OpenbooQ actually sell?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Full residential roof replacement, sold retail, direct to the homeowner, in Ohio and Texas. OpenbooQ does not sell roof repair. Materials are GAF, at wholesale cost, plus one Service and Warranty fee of about 20%. OpenbooQ takes no deposit: nothing is due at signing or during the work, and payment is due only after the roof is installed and approved at final inspection. OpenbooQ publishes one phone number, (937) 910-8791.',
      },
    },
  ],
};

const STEPS = [
  {
    number: '1',
    badge: 'Start Here',
    title: 'Get an OpenbooQ quote first',
    body: (
      <>
        <strong className="font-bold text-white">Before you file,</strong> enter your address for a free, line-itemed quote in about 30 minutes — documenting the real cost of the work.
      </>
    ),
    featured: true,
  },
  {
    number: '2',
    badge: null,
    title: 'File your claim with our quote in hand',
    body: (
      <>
        Submit it to your carrier <strong className="font-bold text-white">at the time of filing,</strong> along with photos of the damage, so they see the actual repair cost from the start.
      </>
    ),
    featured: false,
  },
  {
    number: '3',
    badge: null,
    title: 'Your insurer reviews & settles with you',
    body: (
      <>
        Having the real numbers up front can <strong className="font-bold text-white">speed the whole process along</strong> — then we get your roof done.
      </>
    ),
    featured: false,
  },
];

const StepCard = ({ step, index }) => (
  <motion.article
    className="relative rounded-2xl p-7 lg:p-8 flex flex-col h-full"
    style={{
      background: step.featured
        ? 'linear-gradient(135deg, rgba(7, 176, 233, 0.12) 0%, rgba(7, 176, 233, 0.04) 100%)'
        : 'rgba(255, 255, 255, 0.04)',
      border: step.featured
        ? '1.5px solid rgba(7, 176, 233, 0.55)'
        : '1px solid rgba(255, 255, 255, 0.10)',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      boxShadow: step.featured
        ? '0 16px 40px rgba(7, 176, 233, 0.18), 0 0 0 4px rgba(7, 176, 233, 0.04)'
        : '0 8px 24px rgba(0, 0, 0, 0.20)',
    }}
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.55, delay: 0.55 + index * 0.10, ease: EASE_OUT_EXPO }}
    whileHover={{ y: -4, transition: { duration: 0.25, ease: 'easeOut' } }}
  >
    {/* Numbered badge — cyan rounded square, top of card */}
    <div className="mb-5">
      <div
        className="inline-flex items-center justify-center rounded-xl font-black text-white"
        style={{
          width: '52px',
          height: '52px',
          fontSize: '24px',
          background: '#07B0E9',
          boxShadow: '0 8px 20px rgba(7, 176, 233, 0.35)',
        }}
        aria-hidden="true"
      >
        {step.number}
      </div>
    </div>

    {/* "Start Here" pill — featured card only, sits above the title */}
    {step.badge && (
      <div className="mb-4 flex justify-center">
        <div
          className="inline-flex items-center px-3 py-1 rounded-full text-[10.5px] font-bold uppercase tracking-[0.14em]"
          style={{
            background: 'rgba(7, 176, 233, 0.20)',
            border: '1px solid rgba(7, 176, 233, 0.45)',
            color: '#6cc7ff',
          }}
        >
          {step.badge}
        </div>
      </div>
    )}

    {/* Title */}
    <h3
      className="font-black mb-3 text-center text-white leading-tight"
      style={{
        fontSize: 'clamp(18px, 1.6vw, 22px)',
        fontFamily: "'Poppins', 'Montserrat', sans-serif",
        letterSpacing: '-0.015em',
      }}
    >
      {step.title}
    </h3>

    {/* Body */}
    <p
      className="text-center leading-relaxed"
      style={{
        fontSize: '14.5px',
        color: 'rgba(255, 255, 255, 0.78)',
      }}
    >
      {step.body}
    </p>
  </motion.article>
);

const FAQ_ITEMS = [
  {
    q: 'Does homeowners insurance cover roof replacement?',
    a: (
      <>
        It depends on the cause of the damage and your policy. Insurers generally cover roof damage from{' '}
        <strong className="font-bold text-gray-900">sudden, accidental events</strong>{' '}
        like hail, windstorms, and fallen trees. Damage from age, normal wear, or lack of maintenance is typically not covered. Whether your specific roof is covered, and how much your insurer pays, is determined solely by your carrier under your policy. Always confirm with your insurer.
      </>
    ),
  },
  {
    q: "What's the difference between ACV and RCV on a roof claim?",
    a: (
      <>
        <strong className="font-bold text-gray-900">ACV (Actual Cash Value)</strong>{' '}
        is the depreciated value of your roof — replacement cost minus depreciation for age and wear.{' '}
        <strong className="font-bold text-gray-900">RCV (Replacement Cost Value)</strong>{' '}
        is the full cost to replace it with similar materials. Many policies pay the ACV first, then release the remaining "recoverable depreciation" after the work is completed and documented. Your policy type determines which applies, so confirm with your carrier.
      </>
    ),
  },
  {
    q: 'How does my deductible work on a roof claim?',
    a: (
      <>
        Your deductible is the amount you pay out of pocket before your insurance contributes. If your approved claim is $14,000 and your deductible is $2,000, your insurer pays $12,000 and you pay $2,000.{' '}
        <strong className="font-bold text-gray-900">You are responsible for paying your deductible</strong>{' '}
        — it can't legally be waived or covered by a contractor, and a homeowner cannot profit from an insurance claim.
      </>
    ),
  },
  {
    q: 'Should I get a roofing quote before or after I file my claim?',
    a: (
      <>
        We recommend getting an{' '}
        <strong className="font-bold text-gray-900">OpenbooQ quote first, then filing your claim with that quote in hand.</strong>{' '}
        Submitting a transparent, line-itemed quote to your carrier at the time of filing means they see the actual cost of the work from the start, which can help speed up the process.
      </>
    ),
  },
  {
    q: 'Will filing a roof insurance claim raise my premium?',
    a: (
      <>
        It can, depending on your insurer, your claims history, and whether the damage came from a widespread weather event. Many carriers treat weather-related claims differently from other types. Because this varies,{' '}
        <strong className="font-bold text-gray-900">ask your insurance company directly</strong>{' '}
        how a claim might affect your premium before you file.
      </>
    ),
  },
  {
    q: 'What if my roof insurance claim is denied?',
    a: (
      <>
        If your claim is denied, your insurer must provide a reason. You can request a re-inspection, provide additional documentation (photos, a detailed contractor estimate), or use your carrier's appeals process. OpenbooQ doesn't negotiate with adjusters, but our transparent line-itemed quote can serve as clear documentation of the actual repair cost. For disputes, you may also consult a{' '}
        <strong className="font-bold text-gray-900">licensed public adjuster.</strong>
      </>
    ),
  },
  {
    q: 'How long do I have to file a claim after storm damage?',
    a: (
      <>
        Most policies have a filing deadline after the date of loss — often several months to a year, but it varies by carrier and state.{' '}
        <strong className="font-bold text-gray-900">File as soon as you reasonably can</strong>{' '}
        after discovering damage; delays can complicate a claim. Check your specific policy or ask your carrier for the exact deadline.
      </>
    ),
  },
  {
    q: 'Does OpenbooQ work directly with my insurance company?',
    a: (
      <>
        No. OpenbooQ is a roofing provider, not an insurance company, public adjuster, or claims representative. We{' '}
        <strong className="font-bold text-gray-900">don't file, manage, negotiate, or settle claims,</strong>{' '}
        and we don't work directly with adjusters. What we provide is a free, transparent, line-itemed quote you can bring to your insurer yourself when you file.
      </>
    ),
  },
  {
    q: 'Why does a transparent quote help with my insurance claim?',
    a: (
      <>
        Insurance adjusters often use Xactimate or QuickMeasure-style pricing — the same methodology OpenbooQ uses. Because the pricing reconciles cleanly, filing your claim with an OpenbooQ quote in hand means{' '}
        <strong className="font-bold text-gray-900">your carrier sees the actual cost from the start,</strong>{' '}
        which can reduce back-and-forth and help speed up the process.
      </>
    ),
  },
  {
    q: 'Do I have to use the contractor my insurance recommends?',
    a: (
      <>
        No. In most cases{' '}
        <strong className="font-bold text-gray-900">you have the right to choose your own roofing contractor.</strong>{' '}
        Your insurer may suggest a preferred vendor, but the decision is generally yours. OpenbooQ is happy to provide a transparent quote so you can compare and choose with full information.
      </>
    ),
  },
];

const FAQItem = ({ question, answer, isOpen, onToggle, index }) => (
  <motion.div
    className="rounded-2xl transition-all duration-300 hover:border-[#94d6f3]"
    style={{
      background: isOpen ? 'rgba(7, 176, 233, 0.06)' : '#ffffff',
      border: isOpen ? '1.5px solid rgba(7, 176, 233, 0.45)' : '1px solid #e5ebf0',
      boxShadow: isOpen
        ? '0 8px 24px rgba(7, 176, 233, 0.08)'
        : '0 1px 2px rgba(15, 23, 42, 0.03)',
    }}
    initial={{ opacity: 0, y: 12 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.1 }}
    transition={{ duration: 0.45, delay: 0.03 + index * 0.04, ease: [0.16, 1, 0.3, 1] }}
  >
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={isOpen}
      className="w-full flex items-center justify-between gap-4 px-5 py-4 lg:px-6 lg:py-5 text-left"
    >
      <span
        className="font-bold text-[15px] lg:text-base leading-snug"
        style={{ color: '#0a1628' }}
      >
        {question}
      </span>
      <div
        className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300"
        style={{
          background: isOpen ? '#07B0E9' : 'rgba(15, 23, 42, 0.05)',
          color: isOpen ? '#ffffff' : '#64748b',
        }}
        aria-hidden="true"
      >
        {isOpen ? <X size={16} strokeWidth={2.6} /> : <Plus size={16} strokeWidth={2.6} />}
      </div>
    </button>
    {isOpen && (
      <div
        className="px-5 pb-5 lg:px-6 lg:pb-6 text-[14.5px] lg:text-[15px] leading-relaxed"
        style={{ color: '#475569' }}
      >
        {answer}
      </div>
    )}
  </motion.div>
);

const InsurancePage = () => {
  // Empty set on load → ALL FAQ items collapsed by default per user.
  const [openFaqs, setOpenFaqs] = useState(() => new Set());

  const toggleFaq = (idx) => {
    setOpenFaqs((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleTalkToRoofer = (e) => {
    e.preventDefault();
    window.location.href = '/support';
  };

  return (
    <>
      {/* TITLE/DESCRIPTION CANON — this page is educational, and the title and
          description are the ONLY thing many answer engines read before they
          summarise it. The previous pair ("Insurance Claims, Made Clear") read
          as a service offer, and answer engines duly told homeowners OpenbooQ
          handles claims. It does not: OpenbooQ is retail-only, and implying
          claim handling is a licensing problem under Tex. Ins. Code ch. 4102
          (public insurance adjusters). The disclaimer is now IN the title and
          the first line of the description, not only in the "What OpenbooQ
          doesn't do" card two thirds of the way down the page. */}
      <Helmet>
        <title>Roof Insurance Claims Explained — We Don't File Them | OpenbooQ</title>
        <meta
          name="description"
          content="OpenbooQ does not file, negotiate, or manage insurance claims and is not a public adjuster. This is plain-English education on how roof insurance claims work, plus a free itemized quote you can take to your own carrier when you file."
        />
        <meta property="og:title" content="Roof Insurance Claims Explained — We Don't File Them | OpenbooQ" />
        <meta property="og:description" content="OpenbooQ does not file, negotiate, or manage insurance claims and is not a public adjuster. Plain-English education on how roof insurance claims work — plus a free itemized quote to file with." />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(INSURANCE_SCOPE_JSONLD)}</script>
      </Helmet>

      <div className="min-h-screen bg-white flex flex-col">
        <Header />

        {/* ═══ HERO — "Roof insurance claims, made clear."
            Photo bg (roofing-companies-dayton-ohio) + heavy dark
            navy overlay + cyan ambient glow. Same overlay vocabulary
            as the /how-it-works hero so the photo reads as texture
            while keeping all white text crisply legible.
            Eyebrow → headline → subhead → 3 numbered step cards
            with a thin cyan connector line between them →
            "Start with my free quote" white pill CTA → tiny
            microcopy with a "talk to a roofer" link. */}
        <section
          className="relative overflow-hidden pt-48 sm:pt-36 lg:pt-44 pb-20 lg:pb-28"
          style={{
            backgroundImage: `url('/assets/images/insurance-hero.webp')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundColor: '#0a0a0a',
          }}
        >
          {/* Overlay over the photo. Changed 2026-05-27 from heavy
              navy (88%/82%) to lighter black (62%/52%) per user —
              same darkening role, but pure-black tone (no blue cast)
              and lighter opacity so the roof photo reads more
              through. Still enough darkening to keep the white
              headline / subhead / step cards crisply legible. */}
          <div
            className="absolute inset-0 pointer-events-none"
            aria-hidden="true"
            style={{
              background:
                'linear-gradient(135deg, rgba(0, 0, 0, 0.62) 0%, rgba(0, 0, 0, 0.52) 100%)',
            }}
          />
          {/* Cyan ambient glow center-top */}
          <div
            className="absolute pointer-events-none"
            aria-hidden="true"
            style={{
              top: '10%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '60%',
              height: '50%',
              background: 'radial-gradient(ellipse, rgba(7,176,233,0.18) 0%, transparent 70%)',
              filter: 'blur(40px)',
            }}
          />

          <div className="relative max-w-6xl mx-auto px-5 lg:px-8 text-center text-white">
            {/* Eyebrow pill */}
            <motion.div
              className="mb-7 flex justify-center"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: EASE_OUT_EXPO }}
            >
              <div
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.14em]"
                style={{
                  background: 'rgba(7, 176, 233, 0.18)',
                  border: '1px solid rgba(7, 176, 233, 0.45)',
                  color: '#6cc7ff',
                }}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ background: '#10B981' }}
                  aria-hidden="true"
                />
                If You Read Nothing Else, Read This
              </div>
            </motion.div>

            {/* Two-tone headline */}
            <h1
              className="font-black leading-[1.05] tracking-tight mb-6"
              style={{
                fontSize: 'clamp(38px, 6vw, 72px)',
                fontFamily: "'Poppins', 'Montserrat', sans-serif",
                letterSpacing: '-0.025em',
              }}
            >
              <motion.span
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.65, delay: 0.08, ease: EASE_OUT_EXPO }}
                className="inline"
              >
                Roof insurance claims,{' '}
              </motion.span>
              <motion.span
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.65, delay: 0.22, ease: EASE_OUT_EXPO }}
                style={{ color: '#07B0E9' }}
                className="inline"
              >
                made clear.
              </motion.span>
            </h1>

            {/* Subhead */}
            <motion.p
              className="mx-auto mb-12 max-w-2xl leading-relaxed"
              style={{
                fontSize: 'clamp(15px, 1.4vw, 18px)',
                color: 'rgba(255, 255, 255, 0.85)',
              }}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.4, ease: EASE_OUT_EXPO }}
            >
              The single most important thing to know:{' '}
              <strong className="font-bold text-white">get your OpenbooQ quote before you file.</strong>{' '}
              Here's the whole process in three steps.
            </motion.p>

            {/* SCOPE NOTE — above the fold, on purpose.
                What OpenbooQ does and does not do used to be stated only in the
                "What OpenbooQ doesn't do" card two thirds of the way down. A
                homeowner skimming on a phone, and an answer engine summarising
                the page, both stopped long before it. Same facts, said first.
                Plain <p> text so it survives prerendering and text extraction. */}
            <motion.div
              className="mx-auto mb-12 max-w-2xl rounded-2xl px-5 py-4 text-left"
              style={{
                background: 'rgba(255, 255, 255, 0.06)',
                border: '1px solid rgba(255, 255, 255, 0.16)',
              }}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5, ease: EASE_OUT_EXPO }}
            >
              <p
                className="leading-relaxed"
                style={{ fontSize: 'clamp(13.5px, 1.15vw, 15px)', color: 'rgba(255,255,255,0.86)' }}
              >
                <strong className="font-bold text-white">To be clear about what we do:</strong>{' '}
                OpenbooQ <strong className="font-bold text-white">does not file, negotiate, adjust, or manage
                insurance claims</strong>, does not work directly with adjusters, does not determine coverage,
                and is <strong className="font-bold text-white">not a public adjuster</strong> or an insurance
                representative. You file your own claim with your own carrier. What we sell is{' '}
                <strong className="font-bold text-white">full roof replacement</strong> — not roof repair — with
                no deposit and nothing due until the finished roof passes your inspection. Everything on this
                page is general educational information, not insurance advice or a coverage determination.
              </p>
            </motion.div>

            {/* 3 step cards — 3-col on lg+, stacks on mobile.
                Thin cyan connector lines between cards (hidden on
                mobile where the stack is vertical). */}
            <div className="relative">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 lg:gap-3 items-stretch">
                {STEPS.map((step, i) => (
                  <React.Fragment key={step.number}>
                    <StepCard step={step} index={i} />
                    {i < STEPS.length - 1 && (
                      <div
                        className="hidden lg:block absolute pointer-events-none"
                        aria-hidden="true"
                        style={{
                          top: '90px',
                          left: `${33.33 * (i + 1)}%`,
                          transform: 'translateX(-50%)',
                          width: '40px',
                          height: '1.5px',
                          background:
                            'linear-gradient(90deg, rgba(7, 176, 233, 0.20) 0%, rgba(7, 176, 233, 0.55) 50%, rgba(7, 176, 233, 0.20) 100%)',
                        }}
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* CTA — white pill */}
            <motion.div
              className="mt-12 flex justify-center"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.95, ease: EASE_OUT_EXPO }}
            >
              <button
                type="button"
                onClick={() => openCustomerPortal()}
                className="group inline-flex items-center gap-2.5 font-bold px-7 py-3.5 rounded-full text-[15px] bg-white transition-transform duration-200 hover:-translate-y-0.5"
                style={{
                  color: '#0a1628',
                  boxShadow: '0 10px 24px rgba(7, 176, 233, 0.25), 0 4px 12px rgba(0, 0, 0, 0.20)',
                }}
              >
                Start with my free quote
                <ArrowRight
                  size={16}
                  strokeWidth={2.6}
                  className="transition-transform duration-200 group-hover:translate-x-1"
                  aria-hidden="true"
                />
              </button>
            </motion.div>

            {/* Microcopy under CTA */}
            <motion.p
              className="mt-6 text-sm"
              style={{ color: 'rgba(255, 255, 255, 0.65)' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 1.08, ease: EASE_OUT_EXPO }}
            >
              Free · No obligation · About 30 minutes ·{' '}
              <a
                href="/support"
                onClick={handleTalkToRoofer}
                className="font-semibold hover:underline"
                style={{ color: '#07B0E9' }}
              >
                or talk to a roofer
              </a>
            </motion.p>
          </div>
        </section>

        {/* ═══ "WHY IT WORKS" — cyan gradient card with outer halo,
            2-col layout. Left: eyebrow + headline + body + CTA.
            Right: translucent glass "What We Provide" panel with
            3 checkmark bullets. Same vocabulary as the storm-damage
            callout on /support so the cards read as one design
            language. */}
        <section className="py-16 lg:py-24" style={{ background: '#F8FAFC' }}>
          <div className="max-w-[1180px] mx-auto px-5 lg:px-8">
            <motion.div
              className="relative rounded-3xl overflow-hidden p-7 lg:p-12"
              style={{
                background: 'linear-gradient(135deg, #0a6eb8 0%, #07B0E9 45%, #21c0e9 100%)',
                boxShadow:
                  '0 0 60px 8px rgba(7, 176, 233, 0.25), 0 24px 60px rgba(7, 176, 233, 0.22)',
              }}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, ease: EASE_OUT_EXPO }}
            >
              <div className="relative grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-8 lg:gap-12 items-start">
                {/* Left column */}
                <div>
                  {/* Eyebrow with shield icon */}
                  <div
                    className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[10.5px] font-bold uppercase tracking-[0.14em] mb-6"
                    style={{
                      background: 'rgba(255, 255, 255, 0.12)',
                      border: '1px solid rgba(255, 255, 255, 0.28)',
                      color: 'rgba(255, 255, 255, 0.96)',
                    }}
                  >
                    <Shield size={13} strokeWidth={2.4} aria-hidden="true" />
                    Why It Works
                  </div>

                  {/* Headline */}
                  <h2
                    className="font-black leading-[1.1] tracking-tight mb-5 text-white"
                    style={{
                      fontSize: 'clamp(28px, 3.4vw, 44px)',
                      fontFamily: "'Poppins', 'Montserrat', sans-serif",
                      letterSpacing: '-0.02em',
                    }}
                  >
                    Your transparent quote speaks the adjuster's language.
                  </h2>

                  {/* Body — specific bold phrases per mockup */}
                  <p
                    className="leading-relaxed mb-7"
                    style={{
                      fontSize: '15px',
                      color: 'rgba(255, 255, 255, 0.94)',
                    }}
                  >
                    Insurance adjusters often price claims using Xactimate or QuickMeasure-style methodology —{' '}
                    <strong className="font-bold text-white">the same approach OpenbooQ uses.</strong>{' '}
                    Because the numbers reconcile cleanly, filing with our line-itemed quote in hand means your carrier sees the actual cost of the work from the start, with less back-and-forth.
                  </p>

                  {/* CTA — white pill, cyan text, arrow icon */}
                  <button
                    type="button"
                    onClick={() => openCustomerPortal()}
                    className="group inline-flex items-center gap-2 font-bold px-6 py-3.5 rounded-full text-[14.5px] bg-white transition-transform duration-200 hover:-translate-y-0.5"
                    style={{
                      color: '#0a6eb8',
                      boxShadow: '0 10px 24px rgba(10, 22, 40, 0.22)',
                    }}
                  >
                    Get my free quote
                    <ArrowRight
                      size={16}
                      strokeWidth={2.6}
                      className="transition-transform duration-200 group-hover:translate-x-1"
                      aria-hidden="true"
                    />
                  </button>
                </div>

                {/* Right column — "WHAT WE PROVIDE" glass panel */}
                <div
                  className="rounded-2xl p-6 lg:p-7"
                  style={{
                    background: 'rgba(255, 255, 255, 0.10)',
                    border: '1px solid rgba(255, 255, 255, 0.22)',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                  }}
                >
                  <p
                    className="text-[11px] font-bold uppercase tracking-[0.16em] mb-5"
                    style={{ color: 'rgba(255, 255, 255, 0.80)' }}
                  >
                    What We Provide
                  </p>
                  <ul className="space-y-5">
                    {[
                      {
                        bold: 'A free, line-itemed quote',
                        rest: ' documenting the real cost of the work.',
                      },
                      {
                        bold: null,
                        prefix: 'Pricing built from ',
                        boldMid: 'actual material and labor cost',
                        suffix: ' — no markups.',
                      },
                      {
                        bold: null,
                        prefix: 'A clear document ',
                        boldMid: 'you bring to your insurer',
                        suffix: ' yourself.',
                      },
                    ].map((item, i) => (
                      <li key={i} className="flex gap-3.5 items-start">
                        {/* Check icon in a circle */}
                        <div
                          className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center mt-0.5"
                          style={{
                            background: 'rgba(255, 255, 255, 0.20)',
                            border: '1px solid rgba(255, 255, 255, 0.35)',
                          }}
                          aria-hidden="true"
                        >
                          <Check size={13} strokeWidth={2.8} color="white" />
                        </div>
                        <p
                          className="text-[14.5px] leading-relaxed"
                          style={{ color: 'rgba(255, 255, 255, 0.92)' }}
                        >
                          {item.bold ? (
                            <>
                              <strong className="font-bold text-white">{item.bold}</strong>
                              {item.rest}
                            </>
                          ) : (
                            <>
                              {item.prefix}
                              <strong className="font-bold text-white">{item.boldMid}</strong>
                              {item.suffix}
                            </>
                          )}
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ═══ "THE BASICS" — Does homeowners insurance cover roof
            replacement? Two-card comparison: Typically Covered
            (green) vs Typically Not Covered (gray). Centered
            disclaimer below. Section sits on the same light gray
            (#F8FAFC) as the cyan callout above for visual
            continuity. */}
        <section className="py-16 lg:py-24" style={{ background: '#F8FAFC' }}>
          <div className="max-w-[1180px] mx-auto px-5 lg:px-8">
            {/* Section header */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={{ hidden: {}, visible: {} }}
              className="text-center mb-12 lg:mb-16"
            >
              <motion.div
                variants={{
                  hidden: { opacity: 0, y: 8 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: EASE_OUT_EXPO } },
                }}
                className="mb-5 flex justify-center"
              >
                <div
                  className="inline-flex items-center px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.14em]"
                  style={{
                    background: 'rgba(7, 176, 233, 0.10)',
                    border: '1px solid rgba(7, 176, 233, 0.30)',
                    color: '#0a6eb8',
                  }}
                >
                  The Basics
                </div>
              </motion.div>

              <motion.h2
                variants={{
                  hidden: { opacity: 0, y: 16 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.55, delay: 0.08, ease: EASE_OUT_EXPO } },
                }}
                className="font-black leading-[1.1] tracking-tight mb-5 mx-auto"
                style={{
                  fontSize: 'clamp(30px, 4vw, 50px)',
                  fontFamily: "'Poppins', 'Montserrat', sans-serif",
                  letterSpacing: '-0.025em',
                  color: '#0a1628',
                  maxWidth: '900px',
                }}
              >
                Does homeowners insurance cover{' '}
                <span style={{ color: '#07B0E9' }}>roof replacement?</span>
              </motion.h2>

              <motion.p
                variants={{
                  hidden: { opacity: 0, y: 12 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.5, delay: 0.18, ease: EASE_OUT_EXPO } },
                }}
                className="text-base lg:text-lg text-gray-500 max-w-3xl mx-auto leading-relaxed"
              >
                Often, yes — but it depends entirely on what caused the damage and the terms of your policy. Here's the general rule of thumb most carriers follow. (Your policy is the final word, so always confirm with your insurer.)
              </motion.p>
            </motion.div>

            {/* Two comparison cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-7">
              {/* LEFT — Typically covered (green) */}
              <motion.article
                className="rounded-2xl bg-white overflow-hidden flex flex-col"
                style={{
                  border: '1px solid #e5ebf0',
                  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.04), 0 8px 24px rgba(15, 23, 42, 0.06)',
                }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.55, delay: 0.05, ease: EASE_OUT_EXPO }}
                whileHover={{ y: -4, transition: { duration: 0.25, ease: 'easeOut' } }}
              >
                {/* Green top accent bar */}
                <div style={{ height: '4px', background: '#10B981' }} aria-hidden="true" />

                <div className="p-7 lg:p-8">
                  {/* Header row — green check badge + title */}
                  <div className="flex items-center gap-3 mb-6">
                    <div
                      className="flex items-center justify-center rounded-xl flex-shrink-0"
                      style={{
                        width: '44px',
                        height: '44px',
                        background: 'rgba(16, 185, 129, 0.14)',
                      }}
                      aria-hidden="true"
                    >
                      <Check size={22} strokeWidth={2.6} color="#10B981" />
                    </div>
                    <h3
                      className="font-black"
                      style={{
                        fontSize: 'clamp(20px, 1.8vw, 24px)',
                        color: '#0a1628',
                        fontFamily: "'Poppins', 'Montserrat', sans-serif",
                        letterSpacing: '-0.015em',
                      }}
                    >
                      Typically covered
                    </h3>
                  </div>

                  {/* Bulleted list */}
                  <ul className="space-y-4">
                    {[
                      { bold: 'Hail damage', rest: ' — bruising, granule loss, and impact cracks from a hailstorm.' },
                      { bold: 'Wind damage', rest: ' — shingles lifted, torn, or blown off in a windstorm.' },
                      { bold: 'Fallen trees or limbs', rest: ' — sudden impact damage from a storm.' },
                      { bold: 'Other sudden, accidental events', rest: ' — the key word insurers look for is "sudden."' },
                    ].map((item, i) => (
                      <li key={i} className="flex gap-3 items-start">
                        <Check
                          size={18}
                          strokeWidth={2.8}
                          color="#10B981"
                          className="flex-shrink-0 mt-0.5"
                          aria-hidden="true"
                        />
                        <p className="text-[14.5px] lg:text-[15px] leading-relaxed text-gray-700">
                          <strong className="font-bold text-gray-900">{item.bold}</strong>
                          {item.rest}
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.article>

              {/* RIGHT — Typically not covered (gray) */}
              <motion.article
                className="rounded-2xl bg-white overflow-hidden flex flex-col"
                style={{
                  border: '1px solid #e5ebf0',
                  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.04), 0 8px 24px rgba(15, 23, 42, 0.06)',
                }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.55, delay: 0.13, ease: EASE_OUT_EXPO }}
                whileHover={{ y: -4, transition: { duration: 0.25, ease: 'easeOut' } }}
              >
                {/* Gray top accent bar */}
                <div style={{ height: '4px', background: '#cbd5e1' }} aria-hidden="true" />

                <div className="p-7 lg:p-8">
                  {/* Header row — gray X badge + title */}
                  <div className="flex items-center gap-3 mb-6">
                    <div
                      className="flex items-center justify-center rounded-xl flex-shrink-0"
                      style={{
                        width: '44px',
                        height: '44px',
                        background: 'rgba(100, 116, 139, 0.14)',
                      }}
                      aria-hidden="true"
                    >
                      <X size={22} strokeWidth={2.6} color="#64748b" />
                    </div>
                    <h3
                      className="font-black"
                      style={{
                        fontSize: 'clamp(20px, 1.8vw, 24px)',
                        color: '#0a1628',
                        fontFamily: "'Poppins', 'Montserrat', sans-serif",
                        letterSpacing: '-0.015em',
                      }}
                    >
                      Typically not covered
                    </h3>
                  </div>

                  {/* Bulleted list — em-dash bullets instead of icons */}
                  <ul className="space-y-4">
                    {[
                      { bold: 'Age & normal wear', rest: ' — a roof simply reaching the end of its lifespan.' },
                      { bold: 'Lack of maintenance', rest: ' — damage that built up from deferred upkeep.' },
                      { bold: 'Pre-existing damage', rest: ' — problems that existed before the policy or the event.' },
                      { bold: 'Cosmetic-only issues', rest: ' — some policies exclude purely cosmetic damage.' },
                    ].map((item, i) => (
                      <li key={i} className="flex gap-3 items-start">
                        <span
                          className="flex-shrink-0 mt-2.5 text-[#94a3b8] font-bold"
                          style={{ lineHeight: 1, fontSize: '18px' }}
                          aria-hidden="true"
                        >
                          —
                        </span>
                        <p className="text-[14.5px] lg:text-[15px] leading-relaxed text-gray-700">
                          <strong className="font-bold text-gray-900">{item.bold}</strong>
                          {item.rest}
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.article>
            </div>

            {/* Disclaimer */}
            <motion.p
              className="text-center text-xs lg:text-sm text-gray-400 italic max-w-3xl mx-auto leading-relaxed mt-10 lg:mt-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 0.5, delay: 0.3, ease: EASE_OUT_EXPO }}
            >
              This is general educational information, not a coverage determination. Whether your roof is covered — and how much your insurer pays — is decided solely by your insurance carrier under the terms of your policy.
            </motion.p>
          </div>
        </section>

        {/* ═══ "KNOW THE TERMS" — ACV, RCV, Recoverable Depreciation,
            Deductible. 4-card grid explaining each insurance term in
            plain English. 4 cols on lg+, 2 cols on md, 1 col mobile.
            Card 4 has an inset EXAMPLE callout inside it. */}
        <section className="py-16 lg:py-24" style={{ background: '#F8FAFC' }}>
          <div className="max-w-[1280px] mx-auto px-5 lg:px-8">
            {/* Section header */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={{ hidden: {}, visible: {} }}
              className="text-center mb-12 lg:mb-16"
            >
              <motion.div
                variants={{
                  hidden: { opacity: 0, y: 8 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: EASE_OUT_EXPO } },
                }}
                className="mb-5 flex justify-center"
              >
                <div
                  className="inline-flex items-center px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.14em]"
                  style={{
                    background: 'rgba(7, 176, 233, 0.10)',
                    border: '1px solid rgba(7, 176, 233, 0.30)',
                    color: '#0a6eb8',
                  }}
                >
                  Know The Terms
                </div>
              </motion.div>

              <motion.h2
                variants={{
                  hidden: { opacity: 0, y: 16 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.55, delay: 0.08, ease: EASE_OUT_EXPO } },
                }}
                className="font-black leading-[1.1] tracking-tight mb-5 mx-auto"
                style={{
                  fontSize: 'clamp(28px, 4vw, 50px)',
                  fontFamily: "'Poppins', 'Montserrat', sans-serif",
                  letterSpacing: '-0.025em',
                  color: '#0a1628',
                  maxWidth: '900px',
                }}
              >
                ACV, RCV, depreciation &amp; your deductible,{' '}
                <span style={{ color: '#07B0E9' }}>explained simply.</span>
              </motion.h2>

              <motion.p
                variants={{
                  hidden: { opacity: 0, y: 12 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.5, delay: 0.18, ease: EASE_OUT_EXPO } },
                }}
                className="text-base lg:text-lg text-gray-500 max-w-2xl mx-auto leading-relaxed"
              >
                These four terms decide what your claim actually pays. Understanding them before you file means no surprises later.
              </motion.p>
            </motion.div>

            {/* 4-card grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
              {[
                {
                  tag: 'ACV',
                  title: 'Actual Cash Value',
                  body: [
                    (<>The <strong className="font-bold text-gray-900">depreciated</strong> value of your roof — replacement cost minus depreciation for age and wear. An older roof has a lower ACV.</>),
                    'Many policies pay the ACV amount first, up front.',
                  ],
                  example: null,
                },
                {
                  tag: 'RCV',
                  title: 'Replacement Cost Value',
                  body: [
                    (<>The <strong className="font-bold text-gray-900">full cost</strong> to replace your roof with similar materials, with no depreciation deducted.</>),
                    'An RCV policy is designed to cover the complete cost of putting your roof back to new (minus your deductible).',
                  ],
                  example: null,
                },
                {
                  tag: 'Recoverable Depreciation',
                  title: 'The held-back portion',
                  body: [
                    (<>The <strong className="font-bold text-gray-900">difference between ACV and RCV.</strong> On an RCV policy, insurers often pay the depreciated (ACV) amount first and hold back the rest.</>),
                    (<>That held-back "recoverable depreciation" is typically <strong className="font-bold text-gray-900">released after the work is completed and documented</strong> — so completing the job is what unlocks it.</>),
                  ],
                  example: null,
                },
                {
                  tag: 'Deductible',
                  title: 'What you pay',
                  body: [
                    (<>The amount you pay out of pocket before insurance contributes. <strong className="font-bold text-gray-900">You are always responsible for your deductible</strong> — it can't be waived or covered by a contractor.</>),
                  ],
                  example: {
                    label: 'Example',
                    text: '$14,000 approved claim − $2,000 deductible = insurer pays $12,000, you pay $2,000.',
                  },
                },
              ].map((card, i) => (
                <motion.article
                  key={card.tag}
                  className="rounded-2xl bg-white p-6 lg:p-7 flex flex-col"
                  style={{
                    border: '1px solid #e5ebf0',
                    boxShadow: '0 1px 3px rgba(15, 23, 42, 0.04), 0 8px 24px rgba(15, 23, 42, 0.06)',
                  }}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.55, delay: 0.05 + i * 0.07, ease: EASE_OUT_EXPO }}
                  whileHover={{ y: -4, transition: { duration: 0.25, ease: 'easeOut' } }}
                >
                  {/* Tag pill */}
                  <div className="mb-5">
                    <span
                      className="inline-flex items-center px-3 py-1 rounded-full text-[11px] font-bold whitespace-nowrap"
                      style={{
                        background: 'rgba(7, 176, 233, 0.10)',
                        border: '1px solid rgba(7, 176, 233, 0.25)',
                        color: '#0a6eb8',
                      }}
                    >
                      {card.tag}
                    </span>
                  </div>

                  {/* Title */}
                  <h3
                    className="font-black mb-4"
                    style={{
                      fontSize: 'clamp(18px, 1.5vw, 21px)',
                      color: '#0a1628',
                      fontFamily: "'Poppins', 'Montserrat', sans-serif",
                      letterSpacing: '-0.015em',
                    }}
                  >
                    {card.title}
                  </h3>

                  {/* Body paragraphs */}
                  <div className="space-y-3.5 text-[14px] lg:text-[14.5px] leading-relaxed text-gray-600 flex-1">
                    {card.body.map((para, idx) => (
                      <p key={idx}>{para}</p>
                    ))}
                  </div>

                  {/* Optional EXAMPLE inset (deductible card only) */}
                  {card.example && (
                    <div
                      className="rounded-lg p-4 mt-5"
                      style={{ background: '#F1F5F9' }}
                    >
                      <p
                        className="text-[10.5px] font-bold uppercase tracking-[0.14em] mb-2"
                        style={{ color: '#64748b' }}
                      >
                        {card.example.label}
                      </p>
                      <p className="text-[13.5px] leading-relaxed text-gray-700">
                        {card.example.text}
                      </p>
                    </div>
                  )}
                </motion.article>
              ))}
            </div>
          </div>
        </section>

        {/* ═══ "THE FULL WALKTHROUGH" — 5-step vertical timeline
            with numbered cyan circles connected by a vertical line.
            Step 1 has an inline info callout about staying off the
            roof. Each step has bolded keywords in its body. */}
        <section className="py-16 lg:py-24" style={{ background: '#F8FAFC' }}>
          <div className="max-w-[1180px] mx-auto px-5 lg:px-8">
            {/* Section header */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={{ hidden: {}, visible: {} }}
              className="text-center mb-12 lg:mb-16"
            >
              <motion.div
                variants={{
                  hidden: { opacity: 0, y: 8 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: EASE_OUT_EXPO } },
                }}
                className="mb-5 flex justify-center"
              >
                <div
                  className="inline-flex items-center px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.14em]"
                  style={{
                    background: 'rgba(7, 176, 233, 0.10)',
                    border: '1px solid rgba(7, 176, 233, 0.30)',
                    color: '#0a6eb8',
                  }}
                >
                  The Full Walkthrough
                </div>
              </motion.div>

              <motion.h2
                variants={{
                  hidden: { opacity: 0, y: 16 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.55, delay: 0.08, ease: EASE_OUT_EXPO } },
                }}
                className="font-black leading-[1.1] tracking-tight mb-5 mx-auto"
                style={{
                  fontSize: 'clamp(30px, 4.2vw, 54px)',
                  fontFamily: "'Poppins', 'Montserrat', sans-serif",
                  letterSpacing: '-0.025em',
                  color: '#0a1628',
                  maxWidth: '900px',
                }}
              >
                How to file a roof insurance claim,{' '}
                <span style={{ color: '#07B0E9' }}>in full detail.</span>
              </motion.h2>

              <motion.p
                variants={{
                  hidden: { opacity: 0, y: 12 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.5, delay: 0.18, ease: EASE_OUT_EXPO } },
                }}
                className="text-base lg:text-lg text-gray-500 max-w-2xl mx-auto leading-relaxed"
              >
                The 3-step path above is the short version. Here's everything that happens, from the storm to your finished roof.
              </motion.p>
            </motion.div>

            {/* 5-step vertical timeline.
                Layout per step: number column (cyan circle + vertical
                line) on the left, content on the right. The line lives
                inside each step's number column so the connector
                automatically extends from circle → next circle, and
                naturally stops at step 5 (no line after the last). */}
            <div className="max-w-3xl mx-auto">
              {[
                {
                  num: 1,
                  title: 'Document the damage',
                  body: 'After a storm, safely photograph the damage from the ground — missing or lifted shingles, dents, debris, interior leaks or water stains. Note the date of the storm. The more documentation you have, the smoother your claim.',
                  callout: "Don't climb onto a storm-damaged roof yourself — it's unsafe. Photos from the ground are enough to start.",
                },
                {
                  num: 2,
                  title: 'Get an OpenbooQ quote first',
                  body: (
                    <>
                      Enter your address and we'll deliver a{' '}
                      <strong className="font-bold text-gray-900">free, line-itemed quote in about 30 minutes</strong>{' '}
                      using GAF QuickMeasure aerial imagery. This documents the real, transparent cost of the work — the number you'll bring to your carrier.
                    </>
                  ),
                  callout: null,
                },
                {
                  num: 3,
                  title: 'File your claim with the quote in hand',
                  body: (
                    <>
                      Contact your insurance company to file, and{' '}
                      <strong className="font-bold text-gray-900">submit your OpenbooQ quote at the time of filing,</strong>{' '}
                      along with your damage photos. Because adjusters use the same Xactimate/QuickMeasure-style pricing we do, the numbers reconcile cleanly — so your carrier sees the actual cost from the start.
                    </>
                  ),
                  callout: null,
                },
                {
                  num: 4,
                  title: 'The adjuster inspects & your insurer decides',
                  body: (
                    <>
                      Your carrier may send an adjuster to inspect. They determine coverage and the settlement amount under your policy.{' '}
                      <strong className="font-bold text-gray-900">OpenbooQ doesn't negotiate with adjusters</strong>{' '}
                      — but our clear documentation supports an accurate review.
                    </>
                  ),
                  callout: null,
                },
                {
                  num: 5,
                  title: 'We replace your roof, you pay your deductible',
                  body: (
                    <>
                      Once your claim settles, OpenbooQ completes the work to our standards — certified crews, body-cam documented, backed by our 5-year workmanship warranty.{' '}
                      <strong className="font-bold text-gray-900">You're responsible for your deductible;</strong>{' '}
                      your insurer settles the rest directly with you.
                    </>
                  ),
                  callout: null,
                },
              ].map((step, i, arr) => {
                const isLast = i === arr.length - 1;
                return (
                  <motion.div
                    key={step.num}
                    className="group flex gap-5 lg:gap-7"
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.15 }}
                    transition={{ duration: 0.5, delay: 0.05 + i * 0.08, ease: EASE_OUT_EXPO }}
                  >
                    {/* Number column — circle + connector line.
                        Circle scales + adds a glow on parent-row hover
                        via Tailwind group-hover. */}
                    <div className="flex flex-col items-center flex-shrink-0">
                      <div
                        className="flex items-center justify-center rounded-full font-black text-white transition-all duration-300 group-hover:scale-110"
                        style={{
                          width: '52px',
                          height: '52px',
                          fontSize: '20px',
                          background: '#07B0E9',
                          boxShadow: '0 8px 20px rgba(7, 176, 233, 0.30)',
                        }}
                        aria-hidden="true"
                      >
                        {step.num}
                      </div>
                      {!isLast && (
                        <div
                          className="flex-1 w-px my-3"
                          style={{
                            background:
                              'linear-gradient(180deg, rgba(7, 176, 233, 0.45) 0%, rgba(7, 176, 233, 0.20) 100%)',
                            minHeight: '60px',
                          }}
                          aria-hidden="true"
                        />
                      )}
                    </div>

                    {/* Content column */}
                    <div className={`flex-1 ${isLast ? 'pb-0' : 'pb-10 lg:pb-12'}`}>
                      <h3
                        className="font-black mb-3 leading-tight"
                        style={{
                          fontSize: 'clamp(20px, 1.8vw, 24px)',
                          color: '#0a1628',
                          fontFamily: "'Poppins', 'Montserrat', sans-serif",
                          letterSpacing: '-0.015em',
                          marginTop: '12px',
                        }}
                      >
                        {step.title}
                      </h3>
                      <p className="text-[14.5px] lg:text-[15px] text-gray-600 leading-relaxed">
                        {step.body}
                      </p>

                      {/* Optional inline callout (step 1: stay-off-the-roof warning) */}
                      {step.callout && (
                        <div
                          className="flex items-start gap-2.5 rounded-xl p-4 mt-4"
                          style={{
                            background: 'rgba(7, 176, 233, 0.08)',
                            border: '1px solid rgba(7, 176, 233, 0.25)',
                          }}
                        >
                          <Info
                            size={16}
                            strokeWidth={2.2}
                            className="flex-shrink-0 mt-0.5"
                            style={{ color: '#07B0E9' }}
                            aria-hidden="true"
                          />
                          <p
                            className="text-[13.5px] leading-relaxed font-semibold"
                            style={{ color: '#0a6eb8' }}
                          >
                            {step.callout}
                          </p>
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ═══ "CLEAR EXPECTATIONS" — What OpenbooQ does vs doesn't
            do. Same green-vs-gray comparison vocabulary as "The
            Basics" section above, so the two cards on this page
            read as one design language. Bullets here have bold
            phrases in the middle (not the start), so each bullet
            renders as prefix + bold + suffix. */}
        <section className="py-16 lg:py-24" style={{ background: '#F8FAFC' }}>
          <div className="max-w-[1180px] mx-auto px-5 lg:px-8">
            {/* Section header */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={{ hidden: {}, visible: {} }}
              className="text-center mb-12 lg:mb-16"
            >
              <motion.div
                variants={{
                  hidden: { opacity: 0, y: 8 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: EASE_OUT_EXPO } },
                }}
                className="mb-5 flex justify-center"
              >
                <div
                  className="inline-flex items-center px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.14em]"
                  style={{
                    background: 'rgba(7, 176, 233, 0.10)',
                    border: '1px solid rgba(7, 176, 233, 0.30)',
                    color: '#0a6eb8',
                  }}
                >
                  Clear Expectations
                </div>
              </motion.div>

              <motion.h2
                variants={{
                  hidden: { opacity: 0, y: 16 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.55, delay: 0.08, ease: EASE_OUT_EXPO } },
                }}
                className="font-black leading-[1.1] tracking-tight mb-5 mx-auto"
                style={{
                  fontSize: 'clamp(30px, 4vw, 50px)',
                  fontFamily: "'Poppins', 'Montserrat', sans-serif",
                  letterSpacing: '-0.025em',
                  color: '#0a1628',
                  maxWidth: '900px',
                }}
              >
                What OpenbooQ <span style={{ color: '#07B0E9' }}>does</span> &amp; doesn't do.
              </motion.h2>

              <motion.p
                variants={{
                  hidden: { opacity: 0, y: 12 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.5, delay: 0.18, ease: EASE_OUT_EXPO } },
                }}
                className="text-base lg:text-lg text-gray-500 max-w-3xl mx-auto leading-relaxed"
              >
                We keep our role simple and transparent — here's exactly where OpenbooQ fits in the insurance process, and where your carrier takes over.
              </motion.p>
            </motion.div>

            {/* Two comparison cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-7">
              {/* LEFT — What OpenbooQ does (green) */}
              <motion.article
                className="rounded-2xl bg-white overflow-hidden flex flex-col"
                style={{
                  border: '1px solid #e5ebf0',
                  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.04), 0 8px 24px rgba(15, 23, 42, 0.06)',
                }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.55, delay: 0.05, ease: EASE_OUT_EXPO }}
                whileHover={{ y: -4, transition: { duration: 0.25, ease: 'easeOut' } }}
              >
                <div style={{ height: '4px', background: '#10B981' }} aria-hidden="true" />

                <div className="p-7 lg:p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div
                      className="flex items-center justify-center rounded-xl flex-shrink-0"
                      style={{
                        width: '44px',
                        height: '44px',
                        background: 'rgba(16, 185, 129, 0.14)',
                      }}
                      aria-hidden="true"
                    >
                      <Check size={22} strokeWidth={2.6} color="#10B981" />
                    </div>
                    <h3
                      className="font-black"
                      style={{
                        fontSize: 'clamp(20px, 1.8vw, 24px)',
                        color: '#0a1628',
                        fontFamily: "'Poppins', 'Montserrat', sans-serif",
                        letterSpacing: '-0.015em',
                      }}
                    >
                      What OpenbooQ does
                    </h3>
                  </div>

                  <ul className="space-y-4">
                    {[
                      { prefix: 'Provides a ', bold: 'free, line-itemed quote', suffix: ' in about 30 minutes documenting the real cost of the work.' },
                      { prefix: 'Prices transparently from ', bold: 'actual material and labor cost', suffix: ' — no markups, no hidden fees.' },
                      { prefix: 'Gives you a clear document you can ', bold: 'bring to your insurer yourself', suffix: ' when you file.' },
                      { prefix: "Performs the roof replacement to OpenbooQ's standards ", bold: 'once your claim is settled.', suffix: '' },
                    ].map((item, i) => (
                      <li key={i} className="flex gap-3 items-start">
                        <Check
                          size={18}
                          strokeWidth={2.8}
                          color="#10B981"
                          className="flex-shrink-0 mt-0.5"
                          aria-hidden="true"
                        />
                        <p className="text-[14.5px] lg:text-[15px] leading-relaxed text-gray-700">
                          {item.prefix}
                          <strong className="font-bold text-gray-900">{item.bold}</strong>
                          {item.suffix}
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.article>

              {/* RIGHT — What OpenbooQ doesn't do (gray) */}
              <motion.article
                className="rounded-2xl bg-white overflow-hidden flex flex-col"
                style={{
                  border: '1px solid #e5ebf0',
                  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.04), 0 8px 24px rgba(15, 23, 42, 0.06)',
                }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.55, delay: 0.13, ease: EASE_OUT_EXPO }}
                whileHover={{ y: -4, transition: { duration: 0.25, ease: 'easeOut' } }}
              >
                <div style={{ height: '4px', background: '#cbd5e1' }} aria-hidden="true" />

                <div className="p-7 lg:p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div
                      className="flex items-center justify-center rounded-xl flex-shrink-0"
                      style={{
                        width: '44px',
                        height: '44px',
                        background: 'rgba(100, 116, 139, 0.14)',
                      }}
                      aria-hidden="true"
                    >
                      <X size={22} strokeWidth={2.6} color="#64748b" />
                    </div>
                    <h3
                      className="font-black"
                      style={{
                        fontSize: 'clamp(20px, 1.8vw, 24px)',
                        color: '#0a1628',
                        fontFamily: "'Poppins', 'Montserrat', sans-serif",
                        letterSpacing: '-0.015em',
                      }}
                    >
                      What OpenbooQ doesn't do
                    </h3>
                  </div>

                  <ul className="space-y-4">
                    {[
                      { prefix: 'We ', bold: "don't file or manage your insurance claim", suffix: ' — you file directly with your own carrier.' },
                      { prefix: 'We ', bold: "don't work directly with adjusters", suffix: ' or negotiate on your behalf.' },
                      { prefix: 'We ', bold: "don't determine coverage", suffix: ' or guarantee that a claim will be approved.' },
                      { prefix: 'We ', bold: "don't act as a public adjuster", suffix: ' or provide insurance advice.' },
                      { prefix: 'We ', bold: "don't sell roof repairs", suffix: ' — OpenbooQ does full roof replacement only.' },
                    ].map((item, i) => (
                      <li key={i} className="flex gap-3 items-start">
                        <span
                          className="flex-shrink-0 mt-2.5 text-[#94a3b8] font-bold"
                          style={{ lineHeight: 1, fontSize: '18px' }}
                          aria-hidden="true"
                        >
                          —
                        </span>
                        <p className="text-[14.5px] lg:text-[15px] leading-relaxed text-gray-700">
                          {item.prefix}
                          <strong className="font-bold text-gray-900">{item.bold}</strong>
                          {item.suffix}
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.article>
            </div>
          </div>
        </section>

        {/* ═══ "INSURANCE FAQ" — flat accordion list, 10 questions.
            Same accordion pattern as the /support FAQ — all items
            collapsed on load, multiple can be open simultaneously,
            cyan tint when active, plus/X toggle. Ends with a
            "Please note" disclaimer panel. */}
        <section className="py-16 lg:py-24" style={{ background: '#F8FAFC' }}>
          <div className="max-w-4xl mx-auto px-5 lg:px-8">
            {/* Section header */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={{ hidden: {}, visible: {} }}
              className="text-center mb-12 lg:mb-16"
            >
              <motion.div
                variants={{
                  hidden: { opacity: 0, y: 8 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: EASE_OUT_EXPO } },
                }}
                className="mb-5 flex justify-center"
              >
                <div
                  className="inline-flex items-center px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.14em]"
                  style={{
                    background: 'rgba(7, 176, 233, 0.10)',
                    border: '1px solid rgba(7, 176, 233, 0.30)',
                    color: '#0a6eb8',
                  }}
                >
                  Insurance FAQ
                </div>
              </motion.div>

              <motion.h2
                variants={{
                  hidden: { opacity: 0, y: 16 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.55, delay: 0.08, ease: EASE_OUT_EXPO } },
                }}
                className="font-black leading-[1.1] tracking-tight mb-5 mx-auto"
                style={{
                  fontSize: 'clamp(30px, 4vw, 52px)',
                  fontFamily: "'Poppins', 'Montserrat', sans-serif",
                  letterSpacing: '-0.025em',
                  color: '#0a1628',
                  maxWidth: '880px',
                }}
              >
                Your roof insurance{' '}
                <span style={{ color: '#07B0E9' }}>questions, answered.</span>
              </motion.h2>

              <motion.p
                variants={{
                  hidden: { opacity: 0, y: 12 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.5, delay: 0.18, ease: EASE_OUT_EXPO } },
                }}
                className="text-base lg:text-lg text-gray-500 max-w-2xl mx-auto leading-relaxed"
              >
                The questions homeowners ask most about roof insurance claims.
              </motion.p>
            </motion.div>

            {/* FAQ list — flat (no categories). Items entrance-stagger
                on scroll-in so they cascade as the user scrolls into
                the FAQ section. */}
            <div className="space-y-3">
              {FAQ_ITEMS.map((item, idx) => (
                <FAQItem
                  key={idx}
                  index={idx}
                  question={item.q}
                  answer={item.a}
                  isOpen={openFaqs.has(idx)}
                  onToggle={() => toggleFaq(idx)}
                />
              ))}
            </div>

            {/* "Please note" disclaimer panel */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, ease: EASE_OUT_EXPO }}
              className="rounded-2xl p-6 lg:p-7 mt-12 flex items-start gap-4"
              style={{
                background: '#F1F5F9',
                border: '1px solid #e5ebf0',
              }}
            >
              <div
                className="flex items-center justify-center rounded-full flex-shrink-0"
                style={{
                  width: '38px',
                  height: '38px',
                  background: 'white',
                  border: '1px solid #e5ebf0',
                }}
                aria-hidden="true"
              >
                <Info size={18} strokeWidth={2.2} style={{ color: '#0a1628' }} />
              </div>
              <div>
                <p
                  className="font-black mb-2"
                  style={{
                    fontSize: '15px',
                    color: '#0a1628',
                    fontFamily: "'Poppins', 'Montserrat', sans-serif",
                    letterSpacing: '-0.01em',
                  }}
                >
                  Please note
                </p>
                <p className="text-[13.5px] lg:text-[14px] leading-relaxed text-gray-600">
                  OpenbooQ is a roofing provider, not an insurance company, public adjuster, or claims representative. We do not file, manage, negotiate, or settle insurance claims, and we do not provide insurance or legal advice. Whether storm damage is covered, and how much your insurer pays, is determined solely by your insurance carrier under the terms of your policy. Our quote documents the cost of the roofing work only; it does not guarantee claim approval, coverage, or any particular settlement amount. Always confirm coverage details directly with your insurer.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ═══ FINAL CTA — full-bleed cyan section. Centered
            headline + subhead + two action buttons (white solid
            "Get my free quote" → openCustomerPortal; white outline
            with phone icon "Talk to a roofer" → tel:9379108791).
            Same vocabulary as the "Have a Question About Your
            Warranty?" CTA on /warranty so the closer reads as one
            design language site-wide. */}
        <motion.section
          className="relative overflow-hidden py-20 sm:py-24 lg:py-28"
          style={{
            background: 'linear-gradient(135deg, #07B0E9 0%, #0a6eb8 100%)',
          }}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
          variants={{ hidden: {}, visible: {} }}
        >
          {/* Subtle white grid overlay */}
          <div
            className="absolute inset-0 pointer-events-none"
            aria-hidden="true"
            style={{
              backgroundImage:
                'linear-gradient(rgba(255, 255, 255, 0.06) 1px, transparent 1px),' +
                ' linear-gradient(90deg, rgba(255, 255, 255, 0.06) 1px, transparent 1px)',
              backgroundSize: '60px 60px',
            }}
          />

          <div className="relative max-w-3xl mx-auto px-5 lg:px-8 text-center">
            <motion.h2
              className="font-black leading-[1.05] tracking-tight text-white mb-5"
              style={{
                fontSize: 'clamp(30px, 4.5vw, 52px)',
                fontFamily: "'Poppins', 'Montserrat', sans-serif",
                letterSpacing: '-0.025em',
              }}
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE_OUT_EXPO } },
              }}
            >
              Start with a free, transparent quote.
            </motion.h2>

            <motion.p
              className="leading-relaxed mx-auto mb-10 max-w-2xl"
              style={{
                fontSize: 'clamp(15px, 1.3vw, 18px)',
                color: 'rgba(255, 255, 255, 0.92)',
              }}
              variants={{
                hidden: { opacity: 0, y: 16 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.55, delay: 0.08, ease: EASE_OUT_EXPO } },
              }}
            >
              Get a free, line-itemed OpenbooQ quote in about 30 minutes — the real cost of the work, documented and ready to bring to your insurer.
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-stretch sm:items-center"
              variants={{
                hidden: { opacity: 0, y: 12 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.5, delay: 0.18, ease: EASE_OUT_EXPO } },
              }}
            >
              {/* Primary — white solid pill */}
              <motion.button
                type="button"
                onClick={() => openCustomerPortal()}
                whileHover={{ y: -2 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className="group inline-flex items-center justify-center gap-2.5 px-7 sm:px-8 py-3.5 rounded-full font-bold text-[15px] bg-white"
                style={{
                  color: '#0a1628',
                  boxShadow: '0 10px 24px rgba(10, 22, 40, 0.20)',
                }}
              >
                Get my free quote
                <ArrowRight
                  size={16}
                  strokeWidth={2.6}
                  className="transition-transform duration-200 group-hover:translate-x-1"
                  aria-hidden="true"
                />
              </motion.button>

              {/* Secondary — white outline pill with phone icon */}
              <motion.a
                href="tel:9379108791"
                whileHover={{ y: -2 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className="inline-flex items-center justify-center gap-2.5 px-7 sm:px-8 py-3.5 rounded-full font-bold text-[15px] text-white"
                style={{
                  border: '1.5px solid rgba(255, 255, 255, 0.85)',
                  background: 'transparent',
                }}
              >
                <Phone size={16} strokeWidth={2.4} aria-hidden="true" />
                Talk to a roofer
              </motion.a>
            </motion.div>
          </div>
        </motion.section>

        <Footer />
      </div>
    </>
  );
};

export default InsurancePage;
